import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, map, finalize, tap } from 'rxjs';
import { ConfigService } from '../../_services/config.service';
import { SpinnerService } from '../../_services/SpinnerService';
import {
  DashboardWidget,
  CreateWidgetDto,
  UpdateWidgetDto,
  ReorderWidgetDto,
  WidgetRegistryItem,
  WidgetRegistryItemRaw,
  FolderWidgetConfig,
  FolderChildLayout,
  WidgetConfig,
} from '../../_models/dashboard/dashboard-widget.model';
import {
  UserDashboardConfig,
  AvailableWidgetsResponse,
  AvailableWidgetsResponseRaw,
  UpdateDashboardConfigDto,
  SavePageAsWidgetRequest,
  SaveTableAsWidgetRequest,
  SaveServiceCardAsWidgetRequest,
} from '../../_models/dashboard/dashboard-config.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardConfigService {
  // Subject per reload
  reload = new Subject<boolean>();

  // Stato reattivo per la configurazione dashboard
  private dashboardConfigSubject = new BehaviorSubject<UserDashboardConfig | null>(null);
  dashboardConfig$ = this.dashboardConfigSubject.asObservable();

  // Stato reattivo per i widget
  private widgetsSubject = new BehaviorSubject<DashboardWidget[]>([]);
  widgets$ = this.widgetsSubject.asObservable();

  // Stato modalità edit
  private isEditModeSubject = new BehaviorSubject<boolean>(false);
  isEditMode$ = this.isEditModeSubject.asObservable();

  // Stato caricamento
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private spinnerService: SpinnerService
  ) {}

  /**
   * Trigger reload della dashboard
   */
  setReload(value: boolean): void {
    this.reload.next(value);
  }

  /**
   * Carica la configurazione dashboard dell'utente corrente
   */
  loadDashboardConfig(): Observable<UserDashboardConfig> {
    console.log('=== SERVICE: loadDashboardConfig ===');
    console.log('URL:', `${this.configService.portalUrl}dashboard/config`);
    this.spinnerService.show();
    this.isLoadingSubject.next(true);
    // Reset widget prima del caricamento per evitare dati stale
    console.log('-> Reset widgets a []');
    this.widgetsSubject.next([]);

    return this.http
      .get<UserDashboardConfig>(`${this.configService.portalUrl}dashboard/config`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as UserDashboardConfig),
        tap((config) => {
          console.log('✅ SERVICE: loadDashboardConfig HTTP completato');
          console.log('Widget dal backend:', config?.udc_widgets?.length || 0);
          if (config?.udc_widgets) {
            config.udc_widgets.forEach((w, i) => {
              console.log(`  Backend Widget ${i}: "${w.dw_title}" - pos(${w.dw_grid_col}, ${w.dw_grid_row}) size(${w.dw_col_span}x${w.dw_row_span})`);
            });
          }
          this.dashboardConfigSubject.next(config);
          this.widgetsSubject.next(config?.udc_widgets || []);
        }),
        finalize(() => {
          this.spinnerService.hide();
          this.isLoadingSubject.next(false);
        })
      );
  }

  /**
   * Aggiorna la configurazione dashboard (layout, colonne, tema)
   */
  updateDashboardConfig(config: UpdateDashboardConfigDto): Observable<UserDashboardConfig> {
    this.spinnerService.show();

    return this.http
      .put<UserDashboardConfig>(`${this.configService.portalUrl}dashboard/config`, config, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as UserDashboardConfig),
        tap((updatedConfig) => {
          this.dashboardConfigSubject.next(updatedConfig);
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Recupera la lista dei widget disponibili filtrati per ruolo utente
   */
  getAvailableWidgets(): Observable<AvailableWidgetsResponse> {
    this.spinnerService.show();

    return this.http
      .get<AvailableWidgetsResponseRaw>(`${this.configService.portalUrl}dashboard/widgets/available`, {
        observe: 'response',
      })
      .pipe(
        map((response) => this.mapAvailableWidgetsResponse(response.body as AvailableWidgetsResponseRaw)),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Mappa la risposta raw del backend al formato normalizzato per il frontend
   */
  private mapAvailableWidgetsResponse(raw: AvailableWidgetsResponseRaw): AvailableWidgetsResponse {
    const widgets: WidgetRegistryItem[] = (raw.widgets || []).map((w: WidgetRegistryItemRaw) => ({
      key: w.wr_key,
      name: w.wr_name,
      description: w.wr_description || '',
      icon: w.wr_icon || 'pi pi-box',
      allowedRoles: w.allowedRoles || [],
      defaultConfig: w.defaultConfig || {},
      category: w.wr_category,
      order: w.wr_order,
    }));

    return {
      widgets,
      userWidgetKeys: raw.userWidgetKeys || [],
    };
  }

  /**
   * Recupera i widget dell'utente corrente
   */
  getWidgets(): Observable<DashboardWidget[]> {
    this.spinnerService.show();

    return this.http
      .get<DashboardWidget[]>(`${this.configService.portalUrl}dashboard/widgets`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget[]),
        tap((widgets) => {
          this.widgetsSubject.next(widgets);
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Aggiunge un nuovo widget alla dashboard
   */
  addWidget(widget: CreateWidgetDto): Observable<DashboardWidget> {
    this.spinnerService.show();

    return this.http
      .post<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets`, widget, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((newWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next([...currentWidgets, newWidget]);
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Aggiorna un widget esistente
   */
  updateWidget(widgetId: number, widget: UpdateWidgetDto): Observable<DashboardWidget> {
    this.spinnerService.show();

    return this.http
      .put<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets/${widgetId}`, widget, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((updatedWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          const index = currentWidgets.findIndex((w) => w.dw_id === widgetId);
          if (index !== -1) {
            // Mantieni le posizioni locali che abbiamo inviato, non quelle del backend
            // (il backend potrebbe restituire valori stale)
            const mergedWidget = {
              ...updatedWidget,
              dw_grid_col: widget.dw_grid_col ?? updatedWidget.dw_grid_col,
              dw_grid_row: widget.dw_grid_row ?? updatedWidget.dw_grid_row,
              dw_col_span: widget.dw_col_span ?? updatedWidget.dw_col_span,
              dw_row_span: widget.dw_row_span ?? updatedWidget.dw_row_span,
            };
            currentWidgets[index] = mergedWidget;
            this.widgetsSubject.next([...currentWidgets]);
          }
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Rimuove un widget dalla dashboard
   */
  removeWidget(widgetId: number): Observable<void> {
    this.spinnerService.show();

    return this.http
      .delete<void>(`${this.configService.portalUrl}dashboard/widgets/${widgetId}`, {
        observe: 'response',
      })
      .pipe(
        map(() => undefined),
        tap(() => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next(currentWidgets.filter((w) => w.dw_id !== widgetId));
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Aggiorna l'ordine dei widget (dopo drag-drop)
   */
  reorderWidgets(widgets: DashboardWidget[]): Observable<void> {
    // Aggiorna dw_position su ogni widget in base alla nuova posizione nell'array
    const updatedWidgets = widgets.map((w, index) => ({
      ...w,
      dw_position: index,
    }));

    const reorderData: ReorderWidgetDto[] = updatedWidgets.map((w) => ({
      dw_id: w.dw_id,
      dw_position: w.dw_position,
    }));

    // Aggiorna lo stato locale immediatamente per UX fluida
    this.widgetsSubject.next(updatedWidgets);

    return this.http
      .put<void>(
        `${this.configService.portalUrl}dashboard/widgets/reorder`,
        { widgets: reorderData },
        { observe: 'response' }
      )
      .pipe(map(() => undefined));
  }

  /**
   * Aggiorna le posizioni dei widget nella griglia (batch)
   */
  reorderWidgetsWithPositions(reorderData: ReorderWidgetDto[]): Observable<void> {
    console.log('=== SERVICE: reorderWidgetsWithPositions ===');
    console.log('URL:', `${this.configService.portalUrl}dashboard/widgets/reorder`);
    console.log('Payload:', { widgets: reorderData });
    this.spinnerService.show();

    return this.http
      .put<void>(
        `${this.configService.portalUrl}dashboard/widgets/reorder`,
        { widgets: reorderData },
        { observe: 'response' }
      )
      .pipe(
        tap(() => console.log('✅ SERVICE: reorder HTTP completato')),
        map(() => undefined),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Salva la pagina corrente come widget iframe nella dashboard
   */
  savePageAsWidget(request: SavePageAsWidgetRequest): Observable<DashboardWidget> {
    this.spinnerService.show();

    const widgetData: CreateWidgetDto = {
      dw_type: 'iframe',
      dw_component_key: 'iframe-page',
      dw_title: request.title,
      dw_icon: request.icon || 'pi pi-window-maximize',
      dw_iframe_url: request.url,
      dw_query_params: JSON.stringify(request.queryParams),
      dw_col_span: 6,
      dw_row_span: 3,
    };

    return this.http
      .post<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets/iframe`, widgetData, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((newWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next([...currentWidgets, newWidget]);
          this.configService.openSuccessNotify('Pagina aggiunta alla dashboard');
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Salva una tabella con dati come widget datatable nella dashboard
   */
  saveTableAsWidget(request: SaveTableAsWidgetRequest): Observable<DashboardWidget> {
    this.spinnerService.show();

    const widgetData: CreateWidgetDto = {
      dw_type: 'datatable',
      dw_component_key: 'datatable-widget',
      dw_title: request.title,
      dw_icon: request.icon || 'pi pi-table',
      dw_datatable_config: JSON.stringify(request.datatableConfig),
      dw_col_span: 6,
      dw_row_span: 3,
    };

    return this.http
      .post<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets`, widgetData, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((newWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next([...currentWidgets, newWidget]);
          this.configService.openSuccessNotify('Tabella aggiunta alla dashboard');
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Salva una service card come widget nella dashboard
   */
  saveServiceCardAsWidget(request: SaveServiceCardAsWidgetRequest): Observable<DashboardWidget> {
    this.spinnerService.show();

    const widgetData: CreateWidgetDto = {
      dw_type: 'servicecard',
      dw_component_key: 'servicecard-widget',
      dw_title: request.title,
      dw_icon: request.icon || request.servicecardConfig.srv_icon || 'pi pi-link',
      dw_servicecard_config: JSON.stringify(request.servicecardConfig),
      dw_col_span: 2,
      dw_row_span: 2,
    };

    return this.http
      .post<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets`, widgetData, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((newWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next([...currentWidgets, newWidget]);
          this.configService.openSuccessNotify('Service card aggiunta alla dashboard');
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Resetta la dashboard ai widget di default del ruolo utente.
   * Elimina tutti i widget e ri-provisiona quelli di default.
   */
  resetToDefaults(): Observable<UserDashboardConfig> {
    this.spinnerService.show();
    this.isLoadingSubject.next(true);

    return this.http
      .post<UserDashboardConfig>(`${this.configService.portalUrl}dashboard/reset-defaults`, {}, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as UserDashboardConfig),
        tap((config) => {
          this.dashboardConfigSubject.next(config);
          this.widgetsSubject.next(config?.udc_widgets || []);
          this.configService.openSuccessNotify('Dashboard ripristinata ai valori predefiniti');
        }),
        finalize(() => {
          this.spinnerService.hide();
          this.isLoadingSubject.next(false);
        })
      );
  }

  // ==================== Gestione Stato Edit Mode ====================

  /**
   * Attiva/disattiva la modalità edit
   */
  toggleEditMode(): void {
    this.isEditModeSubject.next(!this.isEditModeSubject.value);
  }

  /**
   * Imposta la modalità edit
   */
  setEditMode(value: boolean): void {
    this.isEditModeSubject.next(value);
  }

  /**
   * Verifica se la modalità edit è attiva
   */
  get isEditMode(): boolean {
    return this.isEditModeSubject.value;
  }

  // ==================== Getters per stato corrente ====================

  /**
   * Ottiene i widget correnti
   */
  get currentWidgets(): DashboardWidget[] {
    return this.widgetsSubject.value;
  }

  /**
   * Ottiene la configurazione corrente
   */
  get currentConfig(): UserDashboardConfig | null {
    return this.dashboardConfigSubject.value;
  }

  // ==================== Utility ====================

  /**
   * Ordina i widget per posizione
   */
  sortWidgetsByPosition(widgets: DashboardWidget[]): DashboardWidget[] {
    return [...widgets].sort((a, b) => a.dw_position - b.dw_position);
  }

  /**
   * Verifica se un widget è già presente nella dashboard
   */
  isWidgetInDashboard(componentKey: string): boolean {
    return this.widgetsSubject.value.some((w) => w.dw_component_key === componentKey);
  }

  // ==================== Folder Widgets ====================

  /**
   * Parsa dw_config di un widget folder. Ritorna null se non è una folder o config invalida.
   */
  parseFolderConfig(widget: DashboardWidget): FolderWidgetConfig | null {
    if (widget.dw_type !== 'folder' || !widget.dw_config) return null;
    try {
      const cfg = typeof widget.dw_config === 'string'
        ? JSON.parse(widget.dw_config as unknown as string)
        : (widget.dw_config as unknown as FolderWidgetConfig);
      if (!cfg || !Array.isArray(cfg.children)) return null;
      const children = (cfg.children as unknown[]).map((id) => Number(id)).filter((id: number) => !isNaN(id));
      const layout = (cfg.layout && typeof cfg.layout === 'object') ? cfg.layout as Record<number, FolderChildLayout> : undefined;
      return { children, layout };
    } catch {
      return null;
    }
  }

  /**
   * Ritorna l'insieme di tutti gli ID widget che sono "figli" di qualche folder.
   */
  getChildWidgetIds(widgets: DashboardWidget[] = this.widgetsSubject.value): Set<number> {
    const ids = new Set<number>();
    for (const w of widgets) {
      const cfg = this.parseFolderConfig(w);
      if (cfg) cfg.children.forEach((id) => ids.add(id));
    }
    return ids;
  }

  /**
   * Ritorna i soli widget top-level (NON dentro nessuna folder).
   * Usato dalla griglia principale per nascondere i child.
   */
  getTopLevelWidgets(widgets: DashboardWidget[] = this.widgetsSubject.value): DashboardWidget[] {
    const childIds = this.getChildWidgetIds(widgets);
    return widgets.filter((w) => !childIds.has(w.dw_id));
  }

  /**
   * Ritorna i widget figli di una specifica folder (nell'ordine di config).
   */
  getFolderChildren(folder: DashboardWidget, widgets: DashboardWidget[] = this.widgetsSubject.value): DashboardWidget[] {
    const cfg = this.parseFolderConfig(folder);
    if (!cfg) return [];
    const byId = new Map(widgets.map((w) => [w.dw_id, w]));
    return cfg.children
      .map((id) => byId.get(id))
      .filter((w): w is DashboardWidget => !!w);
  }

  /**
   * Trova la folder che contiene un widget, se presente.
   */
  getFolderOfWidget(widgetId: number, widgets: DashboardWidget[] = this.widgetsSubject.value): DashboardWidget | null {
    for (const w of widgets) {
      const cfg = this.parseFolderConfig(w);
      if (cfg && cfg.children.includes(widgetId)) return w;
    }
    return null;
  }

  /**
   * Crea una nuova folder che racchiude i widget indicati.
   * La folder viene creata 1x1 (riposizionata dal layout della griglia).
   */
  createFolder(name: string, icon: string, childIds: number[]): Observable<DashboardWidget> {
    const folderConfig: FolderWidgetConfig = { children: childIds };

    const widgetData: CreateWidgetDto = {
      dw_type: 'folder',
      dw_component_key: 'folder',
      dw_title: name,
      dw_icon: icon || 'pi pi-folder',
      dw_col_span: 1,
      dw_row_span: 1,
      dw_config: JSON.stringify(folderConfig) as unknown as WidgetConfig,
    };

    this.spinnerService.show();
    return this.http
      .post<DashboardWidget>(`${this.configService.portalUrl}dashboard/widgets`, widgetData, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.body as DashboardWidget),
        tap((newWidget) => {
          const currentWidgets = this.widgetsSubject.value;
          this.widgetsSubject.next([...currentWidgets, newWidget]);
          this.configService.openSuccessNotify('Folder creata');
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /**
   * Aggiorna i figli di una folder esistente, preservando il layout dei child ancora presenti.
   */
  updateFolderChildren(folder: DashboardWidget, childIds: number[]): Observable<DashboardWidget> {
    const cfg = this.parseFolderConfig(folder);
    const newSet = new Set(childIds);
    let layout: Record<number, FolderChildLayout> | undefined;
    if (cfg?.layout) {
      layout = {};
      for (const [k, v] of Object.entries(cfg.layout)) {
        const id = Number(k);
        if (newSet.has(id)) layout[id] = v;
      }
    }
    return this.saveFolderConfig(folder, { children: childIds, layout });
  }

  /**
   * Aggiunge un widget a una folder esistente.
   */
  addWidgetToFolder(folder: DashboardWidget, widgetId: number): Observable<DashboardWidget> {
    const cfg = this.parseFolderConfig(folder);
    const current = cfg ? cfg.children : [];
    if (current.includes(widgetId)) {
      return this.updateFolderChildren(folder, current);
    }
    return this.updateFolderChildren(folder, [...current, widgetId]);
  }

  /**
   * Rimuove un widget da una folder.
   */
  removeWidgetFromFolder(folder: DashboardWidget, widgetId: number): Observable<DashboardWidget> {
    const cfg = this.parseFolderConfig(folder);
    const current = cfg ? cfg.children : [];
    const layout = cfg?.layout ? { ...cfg.layout } : undefined;
    if (layout) delete layout[widgetId];
    return this.saveFolderConfig(folder, {
      children: current.filter((id) => id !== widgetId),
      layout,
    });
  }

  /**
   * Aggiorna il layout interno della folder (posizione/dimensione di tutti i child).
   */
  updateFolderLayout(folder: DashboardWidget, layout: Record<number, FolderChildLayout>): Observable<DashboardWidget> {
    const cfg = this.parseFolderConfig(folder);
    return this.saveFolderConfig(folder, {
      children: cfg ? cfg.children : [],
      layout,
    });
  }

  /**
   * Salva una FolderWidgetConfig completa sul widget folder (helper privato).
   */
  private saveFolderConfig(folder: DashboardWidget, cfg: FolderWidgetConfig): Observable<DashboardWidget> {
    return this.updateWidget(folder.dw_id, {
      dw_config: JSON.stringify(cfg) as unknown as WidgetConfig,
    });
  }
}
